import Stripe from 'stripe';

/**
 * Catalog base-plan price lookup keys — `studio_{plan}_{interval}_v{n}`, with
 * the archived interim catalog's `test_` prefix, tier segment, and "pro" plan
 * segment tolerated. Mirrors ParseStudioPriceLookupKey in
 * backend/lib/services/stripe_cli/plans/studio_plans.go. The `studio_` prefix
 * is the wire value of the lookup keys and stays as is.
 */
export const BASE_PLAN_LOOKUP_KEY_REGEX =
  /^(?:test_)?studio_(?:freemium|starter|pro|professional|advanced)(?:_\d+)?_(?:monthly|annual)_v\d+$/;

/**
 * Quantity add-on lookup keys (extra credits / extra apps). They ride Classic
 * subscriptions too and identify no plan.
 */
const ADD_ON_LOOKUP_KEY_REGEX = /^(?:test_)?studio_extra_(?:credits|apps)_/;

export const isAddOnPrice = (price?: Stripe.Price | null): boolean =>
  Boolean(price?.lookup_key && ADD_ON_LOOKUP_KEY_REGEX.test(price.lookup_key));

/**
 * The item whose price is the subscription's plan, resolved the way the
 * backend does (FindPlanItemOnSubscription): a base plan wins, otherwise the
 * first non-add-on item, otherwise the first item. Stripe lists items in
 * creation order, so an add-on bought before a plan swap sits first.
 *
 * The backend's current-plan response is the source of truth for the plan;
 * this exists for the window before that response has arrived, so plan gates
 * keep the answer the subscription already gives rather than failing open.
 */
export const getPlanItem = (
  subscription?: Stripe.Subscription,
): Stripe.SubscriptionItem | undefined => {
  const items = subscription?.items?.data ?? [];
  return (
    items.find(
      (item) =>
        item?.price?.lookup_key &&
        BASE_PLAN_LOOKUP_KEY_REGEX.test(item.price.lookup_key),
    ) ??
    items.find((item) => !isAddOnPrice(item?.price)) ??
    items.at(0)
  );
};
